/* C1 — Visão geral (Painel do Cliente).
 *
 * Painel de resumo da empresa: cartões de métrica, volume de mensagens nas
 * últimas 24h e o sentimento das conversas. Variante clara do Nebula. Ligada ao
 * real `/api/metrics/overview`. Sem qualquer menção a infraestrutura — o público
 * é a igreja, não um time de engenharia. */

import React, { useMemo } from 'react';

import { api } from '../api/cliente';
import { Aviso, card, Cabecalho, Nota, Secao } from '../admin/ui';
import { useRecurso } from '../estado/recurso';

const CARTOES = [
  { chave: 'conversas_abertas', rotulo: 'Conversas abertas' },
  { chave: 'aguardando_resposta', rotulo: 'Aguardando resposta' },
  { chave: 'em_atendimento_humano', rotulo: 'Com atendente' },
  { chave: 'mensagens_24h', rotulo: 'Mensagens (24h)' },
];

const SENTIMENTOS = [
  { chave: 'satisfeito', rotulo: 'Satisfeito', cor: 'var(--success)' },
  { chave: 'neutro', rotulo: 'Neutro', cor: 'var(--text-muted)' },
  { chave: 'insatisfeito', rotulo: 'Insatisfeito', cor: 'var(--danger)' },
  { chave: 'sem classificação', rotulo: 'Sem classificação', cor: 'var(--border-strong)' },
];

/** Catmull-Rom → Bézier: curva suave passando por todos os pontos. */
function caminhoSuave(p) {
  if (p.length < 2) return '';
  const d = [`M ${p[0].x} ${p[0].y}`];
  for (let i = 0; i < p.length - 1; i += 1) {
    const p0 = p[i - 1] || p[i];
    const p1 = p[i];
    const p2 = p[i + 1];
    const p3 = p[i + 2] || p2;
    d.push(`C ${p1.x + (p2.x - p0.x) / 6} ${p1.y + (p2.y - p0.y) / 6} ${p2.x - (p3.x - p1.x) / 6} ${p2.y - (p3.y - p1.y) / 6} ${p2.x} ${p2.y}`);
  }
  return d.join(' ');
}

export default function VisaoGeral() {
  const recurso = useRecurso((sinal) => api.metricas(sinal), []);
  const dados = recurso.dados;

  const sentimento = useMemo(() => {
    const soma = {};
    let total = 0;
    for (const e of dados?.emocoes || []) {
      soma[e.emocao] = (soma[e.emocao] || 0) + e.total;
      total += e.total;
    }
    return { soma, total };
  }, [dados]);

  if (recurso.erro) {
    return (
      <div style={{ padding: 24 }}>
        <section style={card}><Aviso titulo="A visão geral não carregou" texto={recurso.erro.mensagem} acao={recurso.erro.temporario ? 'Tentar de novo' : null} aoAcao={recurso.recarregar} /></section>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Cabecalho titulo="Visão geral" descricao="Como andam as conversas e o cuidado da Sofia com a sua gente." />

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 16 }}>
          {CARTOES.map((c) => (
            <div key={c.chave} style={{ ...card, padding: '18px 20px', gap: 6 }}>
              <span className="nb-tab" style={{ font: 'var(--fw-bold) 32px/1 var(--font-display)', color: 'var(--text-primary)' }}>
                {dados ? (dados.cartoes?.[c.chave] ?? 0).toLocaleString('pt-BR') : '—'}
              </span>
              <span style={{ font: 'var(--type-label)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 'var(--ls-caps)' }}>{c.rotulo}</span>
            </div>
          ))}
        </div>

        <Secao titulo="Mensagens nas últimas 24 horas">
          <div style={{ padding: '18px 20px 8px' }}>
            <Grafico volume={dados?.volume || []} />
          </div>
        </Secao>

        <Secao titulo="Sentimento das conversas">
          <div style={{ padding: '18px 20px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {sentimento.total === 0 ? (
              <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>Ainda sem conversas classificadas.</span>
            ) : (
              <>
                <div style={{ display: 'flex', height: 14, borderRadius: 'var(--radius-pill)', overflow: 'hidden', background: 'var(--surface-sunken)' }}>
                  {SENTIMENTOS.map((s) => {
                    const v = sentimento.soma[s.chave] || 0;
                    if (!v) return null;
                    return <span key={s.chave} title={`${s.rotulo}: ${v}`} style={{ width: `${(v / sentimento.total) * 100}%`, background: s.cor }} />;
                  })}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px' }}>
                  {SENTIMENTOS.map((s) => {
                    const v = sentimento.soma[s.chave] || 0;
                    return (
                      <span key={s.chave} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>
                        <span style={{ width: 9, height: 9, borderRadius: 3, background: s.cor }} />
                        {s.rotulo}
                        <span className="nb-tab" style={{ color: 'var(--text-muted)' }}>{v} · {Math.round((v / sentimento.total) * 100)}%</span>
                      </span>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </Secao>
        <Nota>Fonte: /api/metrics/overview, no escopo da sua empresa.</Nota>
      </div>
    </div>
  );
}

function Grafico({ volume }) {
  const W = 760;
  const H = 150;
  const pad = { t: 12, r: 6, b: 8, l: 6 };
  const totais = volume.map((v) => v.total);
  if (!totais.length) return <div style={{ height: H, display: 'flex', alignItems: 'center', color: 'var(--text-muted)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)' }}>Sem dados ainda.</div>;

  const max = Math.max(...totais, 1);
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const pts = totais.map((t, i) => ({
    x: pad.l + (totais.length === 1 ? iw / 2 : (i / (totais.length - 1)) * iw),
    y: pad.t + ih - (t / max) * ih,
  }));
  const linha = caminhoSuave(pts);
  const area = `${linha} L ${pts[pts.length - 1].x} ${pad.t + ih} L ${pts[0].x} ${pad.t + ih} Z`;
  const fim = pts[pts.length - 1];

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img" aria-label="Volume de mensagens por hora">
        <defs>
          <linearGradient id="nbArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((g) => (
          <line key={g} x1={pad.l} x2={W - pad.r} y1={pad.t + ih * g} y2={pad.t + ih * g} stroke="var(--border-subtle)" strokeWidth="1" />
        ))}
        <path d={area} fill="url(#nbArea)" />
        <path d={linha} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        <circle cx={fim.x} cy={fim.y} r="3.5" fill="var(--accent)" />
      </svg>
    </div>
  );
}
